import { computed, onScopeDispose, shallowRef, toValue, watch } from 'vue'
import {
  attrSuffixToFieldType,
  buildAttrFieldKey,
  parseAttrFieldKey,
  resolveCustomAttrDefinition,
} from './custom-attributes.ts'
import { isPackageRemoteOptionFieldId } from './remote-option-fields.ts'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import type { FieldOptionUiRegistry } from './field-option-ui.ts'
import type { FieldOptionItem } from './field-option.ts'
import type { AnySearchFieldId } from './search-field-ids.ts'
import type { FieldType, Locale, SearchField } from './search-schema.ts'

type SearchFieldFilterPlacement = 'primary' | 'more'

/** Host field definition. When `SearchFieldsConfig.fields` is set, that list replaces package defaults. */
export interface SearchFieldConfig {
  id: AnySearchFieldId
  type?: FieldType
  labels?: Partial<Record<Locale, string>>
  category?: 'builtin' | 'attr'
  valuesRemote?: boolean
  /**
   * Cache remote option values across `ensureOptions` calls until the registry changes.
   * Default `true`. Set `false` to refetch whenever options are needed again.
   */
  valuesCache?: boolean
  values?: FieldOptionItem[]
  operators?: string[]
  freetext?: boolean
  valueLabels?: SearchField['valueLabels']
  attrSuffix?: SearchField['attrSuffix']
  /** Used when `filters.primary` / `filters.more` are omitted. */
  filterPlacement?: SearchFieldFilterPlacement
}

export interface SearchFieldsConfig {
  /**
   * Field registry. When omitted, package defaults are used.
   * When set, this list **replaces** defaults — spread {@link DEFAULT_SEARCH_FIELDS} to keep them.
   */
  fields?: SearchFieldConfig[]
  filters?: {
    primary?: AnySearchFieldId[]
    more?: AnySearchFieldId[]
  }
  fetchOptions?: (fieldId: AnySearchFieldId) => Promise<FieldOptionItem[]>
  /**
   * Resolve display metadata for a selected option value (e.g. UUID → user).
   * Return `null`/`undefined` when the host has no resolver for this field.
   */
  resolveOptions?: (
    fieldId: AnySearchFieldId,
    value: string,
  ) => Promise<FieldOptionItem[] | null | undefined>
  searchOptions?: (
    fieldId: AnySearchFieldId,
    query: string,
  ) => Promise<FieldOptionItem[] | null | undefined>
  optionUi?: FieldOptionUiRegistry
}

const DEFAULT_PRIMARY_FIELD_IDS = ['Tag', 'OwnerUser', 'ModifiedTime'] as const

/**
 * Package field definitions. Spread into `SearchFieldsConfig.fields`, then append overrides / extras.
 * Populated when the package schema initializes.
 */
export const DEFAULT_SEARCH_FIELDS: SearchFieldConfig[] = []

/**
 * Package filter lists. Spread / replace in `SearchFieldsConfig.filters`.
 * Populated when the package schema initializes.
 */
export const DEFAULT_SEARCH_FILTERS: {
  primary: AnySearchFieldId[]
  more: AnySearchFieldId[]
} = {
  primary: [...DEFAULT_PRIMARY_FIELD_IDS],
  more: [],
}

export interface ResolvedSearchFieldsRegistry {
  visibleFields: SearchField[]
  fieldsById: Map<string, SearchField>
  fieldsByLowercaseId: Map<string, SearchField>
  primaryFieldIds: readonly AnySearchFieldId[]
  moreFieldIds: readonly AnySearchFieldId[]
  remoteOptionFieldIds: ReadonlySet<string>
  config: SearchFieldsConfig
  resolveField: (fieldId: AnySearchFieldId) => SearchField | undefined
  isRemoteOptionField: (fieldId: AnySearchFieldId) => boolean
}

/** Active registry for imperative getters (`resolveField`, filters, suggestions). Tracked by Vue. */
const activeRegistry = shallowRef<ResolvedSearchFieldsRegistry | null>(null)

const registryChangeListeners = new Set<() => void>()

/** Notify when the active fields registry is replaced (e.g. clear option caches). */
export function onSearchFieldsRegistryChange(listener: () => void): () => void {
  registryChangeListeners.add(listener)
  return () => {
    registryChangeListeners.delete(listener)
  }
}

function notifyRegistryChange(): void {
  for (const listener of registryChangeListeners) {
    listener()
  }
}

function markRemoteOptions(field: SearchField): SearchField {
  return {
    ...field,
    valuesRemote: true,
    values: [],
  }
}

function applyRemoteOptionsFlag(
  field: SearchField,
  remoteIds: Set<string>,
): SearchField {
  return remoteIds.has(field.id) ? markRemoteOptions(field) : field
}

function toSearchFieldConfig(field: SearchField): SearchFieldConfig {
  return {
    id: field.id,
    type: field.type,
    ...(field.labels ? { labels: { ...field.labels } } : {}),
    category: field.customAttr ? 'attr' : 'builtin',
    ...(field.valuesRemote === true ? { valuesRemote: true as const } : {}),
    ...(field.valuesRemote === true ? {} : { values: [...field.values] }),
    ...(field.valuesCache === undefined
      ? {}
      : { valuesCache: field.valuesCache }),
    ...(field.operators ? { operators: [...field.operators] } : {}),
    ...(field.freetext === undefined ? {} : { freetext: field.freetext }),
    ...(field.valueLabels ? { valueLabels: field.valueLabels } : {}),
    ...(field.attrSuffix ? { attrSuffix: field.attrSuffix } : {}),
  }
}

function createFieldFromConfig(config: SearchFieldConfig): SearchField {
  const id = config.id
  const isAttr = id.startsWith('attr.') || config.category === 'attr'
  const parsed = isAttr ? parseAttrFieldKey(id) : null

  return {
    id,
    type:
      config.type ??
      (parsed ? (attrSuffixToFieldType(parsed.suffix) as FieldType) : 'String'),
    category: id === 'Tag' ? 'tag' : 'attribute',
    customAttr: isAttr,
    attrSuffix: config.attrSuffix ?? parsed?.suffix,
    freetext: config.freetext ?? parsed?.suffix === 'string',
    ...(config.labels ? { labels: config.labels } : {}),
    values: config.values ?? [],
    valueLabels: config.valueLabels,
    operators: config.operators,
    valuesRemote: config.valuesRemote,
    ...(config.valuesCache === undefined
      ? {}
      : { valuesCache: config.valuesCache }),
  }
}

function pickDefined<T extends object, K extends keyof T>(
  source: T,
  keys: readonly K[],
): Pick<Partial<T>, K> {
  const result = {} as Pick<Partial<T>, K>
  for (const key of keys) {
    const value = source[key]
    if (value !== undefined) {
      result[key] = value
    }
  }
  return result
}

function mergeFieldConfig(
  base: SearchField,
  patch: SearchFieldConfig,
): SearchField {
  const overrides = pickDefined(patch, [
    'type',
    'labels',
    'values',
    'operators',
    'freetext',
    'valueLabels',
    'attrSuffix',
    'valuesRemote',
    'valuesCache',
  ])

  return {
    ...base,
    ...overrides,
    ...(patch.category === undefined
      ? {}
      : { customAttr: patch.category === 'attr' || Boolean(base.customAttr) }),
  }
}

function buildMoreFieldIdsExcludingPrimary(
  fields: SearchField[],
  primary: readonly string[],
): string[] {
  const primarySet = new Set(primary)
  const seen = new Set<string>()
  const result: string[] = []

  for (const { id } of fields) {
    if (seen.has(id) || primarySet.has(id)) {
      continue
    }
    seen.add(id)
    result.push(id)
  }

  return result
}

function placementByFieldId(
  fieldConfigs: SearchFieldConfig[],
): Map<string, SearchFieldFilterPlacement> {
  const placements = new Map<string, SearchFieldFilterPlacement>()
  for (const field of fieldConfigs) {
    if (field.filterPlacement) {
      placements.set(field.id, field.filterPlacement)
    }
  }
  return placements
}

function resolveFilterLists(
  mergedFields: SearchField[],
  fieldConfigs: SearchFieldConfig[],
  filters?: SearchFieldsConfig['filters'],
): { primary: string[]; more: string[] } {
  const placements = placementByFieldId(fieldConfigs)
  const fieldIds = new Set(mergedFields.map((field) => field.id))

  let primary: string[]
  if (filters?.primary === undefined) {
    primary = DEFAULT_PRIMARY_FIELD_IDS.filter((id) => fieldIds.has(id))
    for (const [fieldId, placement] of placements) {
      if (!fieldIds.has(fieldId)) {
        continue
      }
      if (placement === 'primary' && !primary.includes(fieldId)) {
        primary.push(fieldId)
      }
      if (placement === 'more') {
        primary = primary.filter((id) => id !== fieldId)
      }
    }
  } else {
    primary = [...filters.primary]
  }

  let more: string[]
  if (filters?.more === undefined) {
    more = buildMoreFieldIdsExcludingPrimary(mergedFields, primary)
  } else {
    more = filters.more.filter((id) => !primary.includes(id))
  }

  return { primary, more }
}

function buildRemoteOptionFieldIds(
  mergedFields: SearchField[],
  fieldConfigs: SearchFieldConfig[],
): Set<string> {
  const ids = new Set<string>()

  for (const field of mergedFields) {
    if (isPackageRemoteOptionFieldId(field.id) || field.valuesRemote === true) {
      ids.add(field.id)
    }
  }

  for (const patch of fieldConfigs) {
    if (patch.valuesRemote === true) {
      ids.add(patch.id)
    }
    if (patch.valuesRemote === false) {
      ids.delete(patch.id)
    }
  }

  return ids
}

function synthesizeAttrField(
  fieldId: string,
  remoteIds: Set<string>,
): SearchField | undefined {
  const definition = resolveCustomAttrDefinition(fieldId)
  if (!definition) {
    return undefined
  }

  const id = buildAttrFieldKey(definition.name, definition.suffix)
  return applyRemoteOptionsFlag(
    {
      id,
      type: attrSuffixToFieldType(definition.suffix) as FieldType,
      category: 'attribute',
      customAttr: true,
      attrSuffix: definition.suffix,
      freetext: definition.suffix === 'string',
      ...(definition.labels ? { labels: definition.labels } : {}),
      values: definition.values ?? [],
      valueLabels: definition.valueLabels,
      operators:
        definition.suffix === 'bool'
          ? undefined
          : ['>', '<', '>=', '<=', '=', '!='],
    },
    remoteIds,
  )
}

function buildFieldsFromConfigs(
  fieldConfigs: SearchFieldConfig[],
): Map<string, SearchField> {
  const fieldsById = new Map<string, SearchField>()
  for (const entry of fieldConfigs) {
    const existing = fieldsById.get(entry.id)
    if (existing) {
      fieldsById.set(entry.id, mergeFieldConfig(existing, entry))
    } else {
      fieldsById.set(entry.id, createFieldFromConfig(entry))
    }
  }
  return fieldsById
}

export function resolveSearchFieldsConfig(
  config: SearchFieldsConfig = {},
  defaultFields?: SearchField[],
): ResolvedSearchFieldsRegistry {
  const fieldConfigs = config.fields
  const fieldsById =
    fieldConfigs === undefined
      ? new Map(
          (defaultFields ?? loadDefaultSearchFields()).map((field) => [
            field.id,
            { ...field },
          ]),
        )
      : buildFieldsFromConfigs(fieldConfigs)

  const remoteOptionFieldIds = buildRemoteOptionFieldIds(
    [...fieldsById.values()],
    fieldConfigs ?? [],
  )
  const mergedFields = [...fieldsById.values()].map((field) =>
    applyRemoteOptionsFlag(field, remoteOptionFieldIds),
  )

  const fieldsByIdResolved = new Map(
    mergedFields.map((field) => [field.id, field]),
  )
  const fieldsByLowercaseId = new Map(
    mergedFields.map((field) => [field.id.toLowerCase(), field]),
  )

  const { primary, more } = resolveFilterLists(
    mergedFields,
    fieldConfigs ?? [],
    config.filters,
  )

  function resolveField(fieldId: AnySearchFieldId): SearchField | undefined {
    const direct =
      fieldsByIdResolved.get(fieldId) ??
      fieldsByLowercaseId.get(fieldId.toLowerCase())
    if (direct) {
      return direct
    }

    const definition = resolveCustomAttrDefinition(fieldId)
    if (!definition) {
      return undefined
    }

    const attrId = buildAttrFieldKey(definition.name, definition.suffix)
    return (
      fieldsByIdResolved.get(attrId) ??
      synthesizeAttrField(fieldId, remoteOptionFieldIds)
    )
  }

  return {
    visibleFields: mergedFields,
    fieldsById: fieldsByIdResolved,
    fieldsByLowercaseId,
    primaryFieldIds: primary,
    moreFieldIds: more,
    remoteOptionFieldIds,
    config,
    resolveField,
    isRemoteOptionField: (fieldId) => remoteOptionFieldIds.has(fieldId),
  }
}

const cachedDefaultRegistry: { current: ResolvedSearchFieldsRegistry | null } =
  { current: null }

let defaultFieldsGetter: (() => SearchField[]) | null = null

function refreshDefaultExports(): void {
  const fields = loadDefaultSearchFields()
  DEFAULT_SEARCH_FIELDS.splice(
    0,
    DEFAULT_SEARCH_FIELDS.length,
    ...fields.map((field) => toSearchFieldConfig(field)),
  )
  DEFAULT_SEARCH_FILTERS.primary = [...DEFAULT_PRIMARY_FIELD_IDS]
  DEFAULT_SEARCH_FILTERS.more = buildMoreFieldIdsExcludingPrimary(
    fields,
    DEFAULT_PRIMARY_FIELD_IDS,
  )
}

export function registerDefaultSearchFields(getter: () => SearchField[]): void {
  defaultFieldsGetter = getter
  refreshDefaultExports()
  cachedDefaultRegistry.current = null
}

function isSearchFieldsRegistryInitialized(): boolean {
  return defaultFieldsGetter !== null
}

function loadDefaultSearchFields(): SearchField[] {
  if (!defaultFieldsGetter) {
    throw new Error('Search schema defaults are not initialized.')
  }
  return defaultFieldsGetter()
}

function ensureDefaultRegistry(): ResolvedSearchFieldsRegistry {
  if (!cachedDefaultRegistry.current) {
    cachedDefaultRegistry.current = resolveSearchFieldsConfig()
  }
  return cachedDefaultRegistry.current
}

export function getSearchFieldsRegistry(): ResolvedSearchFieldsRegistry {
  return activeRegistry.value ?? ensureDefaultRegistry()
}

export function setSearchFieldsRegistry(
  registry: ResolvedSearchFieldsRegistry | null,
): void {
  activeRegistry.value = registry
  notifyRegistryChange()
}

/**
 * Provide search field/filter/option config for the current component tree.
 *
 * Accepts a plain object, ref, or getter. Prefer a getter when loaders depend on
 * reactive host context (box / type / node ids) so the registry recomputes and
 * option caches clear when those deps change:
 *
 * ```ts
 * provideSearchFieldsConfig(() => ({
 *   fields: [...DEFAULT_SEARCH_FIELDS],
 *   fetchOptions: id => loadOptions(boxId.value, typeId.value, id),
 *   searchOptions: (id, q) => searchOptions(boxId.value, id, q),
 * }))
 * ```
 *
 * Read reactive values in the getter body (or inside returned callbacks that are
 * recreated when the getter re-runs) so Vue tracks them.
 */
export function provideSearchFieldsConfig(
  config: MaybeRefOrGetter<SearchFieldsConfig> = {},
): ComputedRef<ResolvedSearchFieldsRegistry> {
  const resolved = computed(() => resolveSearchFieldsConfig(toValue(config)))

  watch(
    resolved,
    (registry) => {
      setSearchFieldsRegistry(registry)
    },
    { immediate: true },
  )

  onScopeDispose(() => {
    if (activeRegistry.value === resolved.value) {
      setSearchFieldsRegistry(null)
    }
  })

  return resolved
}

export function getVisibleSearchFields(): SearchField[] {
  return getSearchFieldsRegistry().visibleFields
}

export function getPrimaryFieldIds(): readonly AnySearchFieldId[] {
  return getSearchFieldsRegistry().primaryFieldIds
}

export function getMoreFieldIds(): readonly AnySearchFieldId[] {
  return getSearchFieldsRegistry().moreFieldIds
}

export function isRemoteOptionFieldId(fieldId: AnySearchFieldId): boolean {
  if (isPackageRemoteOptionFieldId(fieldId)) {
    return true
  }
  if (!isSearchFieldsRegistryInitialized()) {
    return false
  }
  return getSearchFieldsRegistry().isRemoteOptionField(fieldId)
}
